import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material';
import { Search, Close, FilterList, Info } from '@mui/icons-material';
import taskCatalog from '../../../services/gmbRanking/task-catalog.json';

const TaskCatalogModal = ({ open, onClose, onSelectTask }) => {
  const [filterAuth, setFilterAuth] = useState('all'); // 'all', 'multi', 'system'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Get all tasks from catalog
  const allTasks = useMemo(() => {
    return Array.isArray(taskCatalog?.tasks) ? taskCatalog.tasks : [];
  }, []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(allTasks.map(task => task.category));
    return ['all', ...Array.from(cats)].filter(Boolean);
  }, [allTasks]);

  // Filter tasks based on selections
  const filteredTasks = useMemo(() => {
    let filtered = [...allTasks];

    // Filter by task auth
    if (filterAuth !== 'all') {
      filtered = filtered.filter(task => {
        const taskAuth = String(task.taskAuth || '').toLowerCase();
        if (filterAuth === 'multi') {
          return taskAuth === 'multi';
        } else if (filterAuth === 'system') {
          return taskAuth === 'system';
        }
        return true;
      });
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(task => task.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(task => 
        String(task.title || '').toLowerCase().includes(query) ||
        String(task.reason || '').toLowerCase().includes(query) ||
        String(task.id || '').toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allTasks, filterAuth, selectedCategory, searchQuery]);

  const handleSelectTask = (task) => {
    onSelectTask(task);
    handleClose();
  };

  const handleClose = () => {
    setFilterAuth('all');
    setSearchQuery('');
    setSelectedCategory('all');
    onClose();
  };

  const getPriorityColor = (priority) => {
    const p = String(priority || '').toLowerCase();
    switch (p) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getTaskAuthColor = (taskAuth) => {
    const auth = String(taskAuth || '').toLowerCase();
    switch (auth) {
      case 'multi': return 'primary';
      case 'system': return 'secondary';
      default: return 'default';
    }
  };

  const getActionModeColor = (actionMode) => {
    const mode = String(actionMode || '').toLowerCase();
    if (mode.includes('dynamic')) return 'success';
    if (mode.includes('system')) return 'info';
    return 'default';
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '2px solid #e0e0e0',
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#2c3e50">
            📋 Task Catalog
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Select a task from the catalog to assign to clients
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Task Auth</InputLabel>
            <Select
              value={filterAuth}
              label="Task Auth"
              onChange={(e) => setFilterAuth(e.target.value)}
              startAdornment={
                <InputAdornment position="start">
                  <FilterList />
                </InputAdornment>
              }
            >
              <MenuItem value="all">All Tasks</MenuItem>
              <MenuItem value="multi">Multi (Manual)</MenuItem>
              <MenuItem value="system">System Driven</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              label="Category"
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} found
          </Typography>
        </Box>

        {/* Tasks Table */}
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(90vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Task Name</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Solution/Description</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', minWidth: 100 }}>Nature</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', minWidth: 100 }}>AI</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', minWidth: 120 }}>Action Mode</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', minWidth: 120 }}>Task Auth</TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: '#f5f5f5', minWidth: 100 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No tasks found matching your filters
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTasks.map((task) => (
                  <TableRow 
                    key={task.id}
                    hover
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'rgba(11, 145, 214, 0.05)'
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {task.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {task.id}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={task.reason || ''}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {task.reason || '-'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.priority || 'Medium'} 
                        size="small" 
                        color={getPriorityColor(task.priority)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.aiEngine === 'none' ? 'No' : 'Yes'} 
                        size="small"
                        color={task.aiEngine === 'none' ? 'default' : 'success'}
                        variant={task.aiEngine === 'none' ? 'outlined' : 'filled'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.actionMode || 'Fix'} 
                        size="small"
                        color={getActionModeColor(task.actionMode)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={task.taskAuth || 'System'} 
                        size="small"
                        color={getTaskAuthColor(task.taskAuth)}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleSelectTask(task)}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskCatalogModal;





